import { Label } from "@components/inputs/field";
import { IGetValueArgs } from "@components/smart/FieldInfo";
import { BirthNumberFieldDef } from "@components/smart/GeneralFieldDefinition";
import SmartField from "@components/smart/smartField";
import BindingContext, { createPath } from "@odata/BindingContext";
import { getPropertyTranslationSync } from "@odata/FieldInfo.utils";
import {
    IPrAtsrFormSectionOtherEmploymentRecordsEntity,
    PrAtsrFormSectionDetailChildDeductionEntity,
    PrAtsrFormSectionDetailZtpEntity,
    PrAtsrFormSectionOtherEmploymentRecordsEntity
} from "@odata/GeneratedEntityTypes";
import { IFormatOptions } from "@odata/OData.utils";
import {
    CheckGroupWrap,
    MonthsSelectorWithSwitchWrapper,
    OtherEmploymentAmountsGrid
} from "@pages/payroll/annualTaxSettlementRequest/AnnualTaxSettlementRequest.styles";
import SmartMonthCheckboxSelector from "@pages/payroll/Eldp/SmartMonthCheckboxSelector";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import i18next from "i18next";
import React, { Fragment } from "react";

import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FastEntryInputSizes, FieldType, GroupedField, LabelStatus, TextAlign } from "../../../enums";
import { TValue } from "../../../global.types";
import CurrencyType from "../../../types/Currency";
import { getUtcDayjs } from "../../../types/Date";
import NumberType, { currencyScaleFormatter } from "../../../types/Number";
import { IFormDef } from "../../../views/formView/Form";
import { IDefinition, IGetDefinition } from "../../PageUtils";

const taxBasePath = BindingContext.localContext("TaxBase");
const advancedTaxBasePath = BindingContext.localContext("AdvancedTaxBase");
const amountsGridPath = BindingContext.localContext("amountsGrid");
const childDeductionTitlePath = BindingContext.localContext("childDeductionTitle");
const ztpsTitlePath = BindingContext.localContext("ZtpsTitle");
export const yearPath = BindingContext.localContext("Year");

function commonAmountFieldsDef(context: IAppContext) {
    return {
        labelStatus: LabelStatus.Removed,
        defaultValue: 0,
        width: BasicInputSizes.M,
        fieldSettings: {
            formatter: currencyScaleFormatter,
            unit: CurrencyType.getCurrencyUnit(getCompanyCurrency(context)),
        }
    };
}

function getLocalContextTranslation(path: string): string {
    switch (path) {
        case taxBasePath:
            return "AnnualTaxSettlementRequest:Form.TaxBaseLabel";
        case advancedTaxBasePath:
            return "AnnualTaxSettlementRequest:Form.AdvancedTaxBaseLabel";
    }
    return null;
}

function getLineNumberByPath(path: string): number {
    switch (path) {
        case PrAtsrFormSectionOtherEmploymentRecordsEntity.IncomeAmount:
            return 1;
        case PrAtsrFormSectionOtherEmploymentRecordsEntity.PaidIncomeAmount:
            return 2;
        case PrAtsrFormSectionOtherEmploymentRecordsEntity.AdditionalIncomeFromFormerPeriodsAmount:
            return 4;
        case taxBasePath:
            return 5;
        case PrAtsrFormSectionOtherEmploymentRecordsEntity.DeductedTaxDepositFromPaidIncomeAmount:
            return 6;
        case PrAtsrFormSectionOtherEmploymentRecordsEntity.DeductedTaxDepositFromAdditionalIncomeAmount:
            return 7;
        case advancedTaxBasePath:
            return 8;
        case PrAtsrFormSectionOtherEmploymentRecordsEntity.SumOfMonthlyBonusAmount:
            return 9;
        default:
            return null;
    }
}

function getMinDate(args: IGetValueArgs): Date {
    const year = args.storage.getValueByPath(yearPath);
    return getUtcDayjs().set("y", year).startOf("year").toDate();
}

function getMaxDate(args: IGetValueArgs): Date {
    const year = args.storage.getValueByPath(yearPath);
    return getUtcDayjs().set("y", year).endOf("year").toDate();
}

export const getDefinition: IGetDefinition = (context): IDefinition => {
    const form: IFormDef = {
        id: "OtherEmploymentSpecialForm",
        translationFiles: getDefinition.translationFiles,
        fieldDefinition: {
            [PrAtsrFormSectionOtherEmploymentRecordsEntity.EmployerName]: {
                label: i18next.t("AnnualTaxSettlementRequest:Form.EmployerName"),
            },
            [PrAtsrFormSectionOtherEmploymentRecordsEntity.DatePeriodFrom]: {
                label: i18next.t("AnnualTaxSettlementRequest:Form.ForPeriod"),
                groupedField: GroupedField.MultiStart,
                isRequired: true,
                fieldSettings: {
                    minDate: getMinDate,
                    maxDate: getMaxDate
                }
            },
            [PrAtsrFormSectionOtherEmploymentRecordsEntity.DatePeriodTo]: {
                labelStatus: LabelStatus.Hidden,
                groupedField: GroupedField.MultiEnd,
                isRequired: true,
                fieldSettings: {
                    minDate: getMinDate,
                    maxDate: getMaxDate
                }
            },
            [PrAtsrFormSectionOtherEmploymentRecordsEntity.TaxpayerDeclarationSigned]: {
                type: FieldType.Switch,
                defaultValue: true,
                affectedFields: [{ id: PrAtsrFormSectionOtherEmploymentRecordsEntity.TaxpayerDeclarationMonths }]
            },
            [PrAtsrFormSectionOtherEmploymentRecordsEntity.TaxpayerDeclarationMonths]: {
                type: FieldType.Custom,
                defaultValue: "0".repeat(12),
                render: (args) => {
                    const isSignedBc = args.bindingContext.getParent().navigate(PrAtsrFormSectionOtherEmploymentRecordsEntity.TaxpayerDeclarationSigned);
                    const shouldShowCheckboxes = args.storage.getValue(isSignedBc) !== false;
                    return (<MonthsSelectorWithSwitchWrapper>
                        <SmartField storage={args.storage}
                                    bindingContext={args.bindingContext.getParent().navigate(PrAtsrFormSectionOtherEmploymentRecordsEntity.TaxpayerDeclarationSigned)}
                                    onChange={args.events?.onChange}/>
                        {shouldShowCheckboxes && <CheckGroupWrap>
                            <Label>{args.storage.t("AnnualTaxSettlementRequest:Form.ForThePeriod")}</Label>
                            <SmartMonthCheckboxSelector {...args.props} {...args.events} hideMasterCheckbox/>
                        </CheckGroupWrap>}
                    </MonthsSelectorWithSwitchWrapper>);
                },
            },
            [PrAtsrFormSectionOtherEmploymentRecordsEntity.IncomeAmount]: {
                ...commonAmountFieldsDef(context),
                affectedFields: [{ id: taxBasePath }]
            },
            [PrAtsrFormSectionOtherEmploymentRecordsEntity.PaidIncomeAmount]: {
                ...commonAmountFieldsDef(context),
                affectedFields: [{ id: taxBasePath }]
            },
            [PrAtsrFormSectionOtherEmploymentRecordsEntity.AdditionalIncomeFromFormerPeriodsAmount]: {
                ...commonAmountFieldsDef(context),
                affectedFields: [{ id: taxBasePath }]
            },
            [taxBasePath]: {
                ...commonAmountFieldsDef(context),
                isReadOnly: true,
                formatter: (_: TValue, args: IFormatOptions<IPrAtsrFormSectionOtherEmploymentRecordsEntity>) => {
                    const {
                        PaidIncomeAmount,
                        AdditionalIncomeFromFormerPeriodsAmount
                    } = args.entity;
                    const result = (PaidIncomeAmount || 0) + (AdditionalIncomeFromFormerPeriodsAmount || 0);
                    return NumberType.format(result, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                }
            },
            [PrAtsrFormSectionOtherEmploymentRecordsEntity.DeductedTaxDepositFromPaidIncomeAmount]: {
                ...commonAmountFieldsDef(context),
                affectedFields: [{ id: advancedTaxBasePath }]
            },
            [PrAtsrFormSectionOtherEmploymentRecordsEntity.DeductedTaxDepositFromAdditionalIncomeAmount]: {
                ...commonAmountFieldsDef(context),
                affectedFields: [{ id: advancedTaxBasePath }]
            },
            [PrAtsrFormSectionOtherEmploymentRecordsEntity.SumOfMonthlyBonusAmount]: {
                ...commonAmountFieldsDef(context),
            },
            [advancedTaxBasePath]: {
                ...commonAmountFieldsDef(context),
                formatter: (_: TValue, args: IFormatOptions<IPrAtsrFormSectionOtherEmploymentRecordsEntity>) => {
                    const {
                        DeductedTaxDepositFromPaidIncomeAmount,
                        DeductedTaxDepositFromAdditionalIncomeAmount
                    } = args.entity;
                    const result = (DeductedTaxDepositFromAdditionalIncomeAmount || 0) + (DeductedTaxDepositFromPaidIncomeAmount || 0);
                    return NumberType.format(result, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                },
                isReadOnly: true
            },
            [amountsGridPath]: {
                type: FieldType.Custom,
                render: (args) => {
                    const fields = [
                        PrAtsrFormSectionOtherEmploymentRecordsEntity.IncomeAmount,
                        PrAtsrFormSectionOtherEmploymentRecordsEntity.PaidIncomeAmount,
                        PrAtsrFormSectionOtherEmploymentRecordsEntity.AdditionalIncomeFromFormerPeriodsAmount,
                        taxBasePath,
                        PrAtsrFormSectionOtherEmploymentRecordsEntity.DeductedTaxDepositFromPaidIncomeAmount,
                        PrAtsrFormSectionOtherEmploymentRecordsEntity.DeductedTaxDepositFromAdditionalIncomeAmount,
                        advancedTaxBasePath,
                        PrAtsrFormSectionOtherEmploymentRecordsEntity.SumOfMonthlyBonusAmount
                    ];
                    const entityBc = args.bindingContext.getParent();
                    const fieldBcs = fields.map(f => entityBc.navigate(f));

                    return <OtherEmploymentAmountsGrid>
                        {fieldBcs.map((bc, idx) => {
                            const path = bc.getPath();
                            const number = getLineNumberByPath(path) + ".";
                            const label = bc.isLocal() ? args.storage.t(getLocalContextTranslation(path)) : getPropertyTranslationSync(bc);
                            return (
                                <Fragment key={idx}>
                                    <Label>{number}&nbsp;{label}</Label>
                                    <SmartField storage={args.storage} bindingContext={bc}
                                                fieldProps={{
                                                    textAlign: TextAlign.Right,
                                                }}
                                                onChange={args.events?.onChange}/>
                                </Fragment>
                            );
                        })}
                    </OtherEmploymentAmountsGrid>;
                }
            },
            // child deduction fields
            [childDeductionTitlePath]: {
                type: FieldType.Custom,
                render: (args) => {
                    const title = args.storage.t("AnnualTaxSettlementRequest:Form.ChildDeductionsSectionTitle");
                    return <Label style={{ marginLeft: "-12px" }}>{title}</Label>;
                }
            },
            [createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.ChildDeductions, PrAtsrFormSectionDetailChildDeductionEntity.FirstName)]: {},
            [createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.ChildDeductions, PrAtsrFormSectionDetailChildDeductionEntity.LastName)]: {},
            [createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.ChildDeductions, PrAtsrFormSectionDetailChildDeductionEntity.PersonalIdentificationNumber)]: {
                ...BirthNumberFieldDef
            },
            [createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.ChildDeductions, PrAtsrFormSectionDetailChildDeductionEntity.DateZtpFrom)]: {},
            [createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.ChildDeductions, PrAtsrFormSectionDetailChildDeductionEntity.DateZtpTo)]: {},
            [createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.ChildDeductions, PrAtsrFormSectionDetailChildDeductionEntity.ChildDeductionOrder)]: {
                type: FieldType.ComboBox,
                width: FastEntryInputSizes.XS,
                fieldSettings: {
                    displayName: "Name"
                }
            },
            [createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.ChildDeductions, PrAtsrFormSectionDetailChildDeductionEntity.DateReceivedFrom)]: {},
            [createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.ChildDeductions, PrAtsrFormSectionDetailChildDeductionEntity.DateReceivedTo)]: {},

            // ztps
            [ztpsTitlePath]: {
                type: FieldType.Custom,
                render: (args) => {
                    const title = args.storage.t("AnnualTaxSettlementRequest:Form.ZtpsSectionTitle");
                    return <Label style={{ marginLeft: "-12px" }}>{title}</Label>;
                }
            },
            [createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.Ztps, PrAtsrFormSectionDetailZtpEntity.DisabilityType)]: {
                type: FieldType.ComboBox,
                fieldSettings: {
                    displayName: "Name"
                }
            },
            [createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.Ztps, PrAtsrFormSectionDetailZtpEntity.DateFrom)]: {},
            [createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.Ztps, PrAtsrFormSectionDetailZtpEntity.DateTo)]: {},
            [createPath(PrAtsrFormSectionOtherEmploymentRecordsEntity.Ztps, PrAtsrFormSectionDetailZtpEntity.IsZtpP)]: {},

        },
        groups: [
            {
                id: "Common",
                rows: [
                    [
                        { id: PrAtsrFormSectionOtherEmploymentRecordsEntity.EmployerName },
                        { id: PrAtsrFormSectionOtherEmploymentRecordsEntity.DatePeriodFrom },
                        { id: PrAtsrFormSectionOtherEmploymentRecordsEntity.DatePeriodTo }],
                    [
                        { id: PrAtsrFormSectionOtherEmploymentRecordsEntity.TaxpayerDeclarationMonths }
                    ],
                    [
                        { id: amountsGridPath }
                    ]
                ]
            }, {
                id: "ChildDeduction",
                rows: [[{ id: childDeductionTitlePath }]],
                lineItems: {
                    collection: PrAtsrFormSectionOtherEmploymentRecordsEntity.ChildDeductions,
                    order: "Id",
                    canReorder: false,
                    isItemCloneable: false,
                    showLineNumbers: false,
                    isTheOnlyItemRemovable: true,
                    columns: [
                        { id: PrAtsrFormSectionDetailChildDeductionEntity.FirstName },
                        { id: PrAtsrFormSectionDetailChildDeductionEntity.LastName },
                        { id: PrAtsrFormSectionDetailChildDeductionEntity.PersonalIdentificationNumber },
                        { id: PrAtsrFormSectionDetailChildDeductionEntity.DateZtpFrom },
                        { id: PrAtsrFormSectionDetailChildDeductionEntity.DateZtpTo },
                        { id: PrAtsrFormSectionDetailChildDeductionEntity.ChildDeductionOrder },
                        { id: PrAtsrFormSectionDetailChildDeductionEntity.DateReceivedFrom },
                        { id: PrAtsrFormSectionDetailChildDeductionEntity.DateReceivedTo },
                    ]
                }
            }, {
                id: "Ztps",
                rows: [[{ id: ztpsTitlePath }]],
                lineItems: {
                    collection: PrAtsrFormSectionOtherEmploymentRecordsEntity.Ztps,
                    order: "Id",
                    canReorder: false,
                    isItemCloneable: false,
                    showLineNumbers: false,
                    isTheOnlyItemRemovable: true,
                    columns: [
                        { id: PrAtsrFormSectionDetailZtpEntity.DisabilityType },
                        { id: PrAtsrFormSectionDetailZtpEntity.DateFrom },
                        { id: PrAtsrFormSectionDetailZtpEntity.DateTo },
                        { id: PrAtsrFormSectionDetailZtpEntity.IsZtpP },
                    ]
                }
            }
        ]
    };

    return {
        form
    };
};

getDefinition.translationFiles = ["AnnualTaxSettlementRequest", "Common"];
